import { useFieldValue, useTranslate } from "ra-core";
import { createRef, useCallback, useState } from "react";
import type { ReactCropperElement } from "react-cropper";
import { Cropper } from "react-cropper";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import "cropperjs/dist/cropper.css";

const ImageEditorField = (props: ImageEditorFieldProps) => {
  const translate = useTranslate();
  const { getValues } = useFormContext();
  const source = useFieldValue({ source: props.source }) ?? getValues(props.source);
  const imageUrl =
    typeof source === "string" ? source : (source as { src?: string })?.src;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { type = "image", emptyText, linkPosition = "none" } = props;

  const commonProps = {
    src: imageUrl,
    onClick: () => setIsDialogOpen(true),
    style: { cursor: "pointer" },
    className: `${props.className || ""}`,
  };

  const width = props.width || (type === "avatar" ? 50 : 200);
  const height = props.height || (type === "avatar" ? 50 : 200);

  return (
    <>
      <div
        className={`flex ${
          linkPosition === "right" ? "flex-row" : "flex-col"
        } items-center ${
          linkPosition === "right" ? "gap-2" : "gap-1"
        } rounded p-${props.backgroundImageColor ? "4" : "0"}`}
        style={{
          backgroundColor: props.backgroundImageColor || "transparent",
        }}
      >
        {props.type === "avatar" ? (
          <Avatar
            {...commonProps}
            className={`cursor-pointer`}
            style={{ width, height }}
          >
            <AvatarImage src={imageUrl} />
            <AvatarFallback>{emptyText}</AvatarFallback>
          </Avatar>
        ) : (
          <img
            {...commonProps}
            className="cursor-pointer object-cover"
            style={{ width, height }}
            alt="Editable content"
          />
        )}
        {linkPosition !== "none" && (
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            className="text-xs underline hover:no-underline cursor-pointer text-center"
          >
            {translate("atomic.settings.change")}
          </button>
        )}
      </div>
      <ImageEditorDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        {...props}
      />
    </>
  );
};

const ImageEditorDialog = (props: ImageEditorDialogProps) => {
  const translate = useTranslate();
  const { setValue, handleSubmit } = useFormContext();
  const cropperRef = createRef<ReactCropperElement>();
  const initialValue = useFieldValue({ source: props.source });
  const [file, setFile] = useState<File | undefined>();
  const [imageSrc, setImageSrc] = useState<string | undefined>(
    typeof initialValue === "string"
      ? initialValue
      : (initialValue as { src?: string })?.src,
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const initialImageSrc = typeof initialValue === "string"
    ? initialValue
    : (initialValue as { src?: string })?.src;
  const hasChanges = imageSrc !== initialImageSrc;

  const onDrop = useCallback((files: File[]) => {
    const preview = URL.createObjectURL(files[0]);
    setFile(files[0]);
    setImageSrc(preview);
  }, []);

  const updateImage = () => {
    const cropper = cropperRef.current?.cropper;
    const croppedImage = cropper?.getCroppedCanvas().toDataURL();
    if (croppedImage) {
      setImageSrc(croppedImage);

      const newFile = file ?? new File([], initialValue?.src);
      const updatedValue = {
        src: croppedImage,
        title: newFile.name,
        rawFile: newFile,
      };
      setValue(
        props.source,
        updatedValue,
        { shouldDirty: true },
      );
      setShowConfirmDialog(false);
      props.onClose();

      if (props.onSave) {
        handleSubmit((values) =>
          props.onSave?.({
            ...values,
            [props.source]: updatedValue,
          }),
        )();
      }
    }
  };

  const deleteImage = () => {
    setValue(props.source, null, { shouldDirty: true });
    if (props.onSave) {
      handleSubmit((values) =>
        props.onSave?.({
          ...values,
          [props.source]: null,
          __clearAvatar: true,
        }),
      )();
    }
    setImageSrc(undefined);
    props.onClose();
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      props.onClose();
    }
  };

  const handleDiscard = () => {
    setShowConfirmDialog(false);
    setImageSrc(initialImageSrc);
    props.onClose();
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg", ".png"] },
    onDrop,
    maxFiles: 1,
  });

  return (
    <Dialog open={props.open} onOpenChange={handleClose}>
      {props.type === "avatar" && (
        <style>
          {`
                        .cropper-crop-box,
                        .cropper-view-box {
                            border-radius: 50%;
                        }
                    `}
        </style>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("atomic.image.uploadAndResize", {
              _: "Enviar e redimensionar imagem",
            })}
          </DialogTitle>
          <DialogDescription>
            {translate("atomic.image.uploadHint", {
              _: "Selecione uma imagem para ajustar e salvar.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 justify-center">
          <div
            className="flex flex-row justify-center bg-gray-50 cursor-pointer p-4 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <p className="text-gray-600">
              {translate("atomic.image.dropOrClick", { _: "Solte um arquivo aqui ou clique para selecionar." })}
            </p>
          </div>

          {imageSrc && (
            <div className="flex justify-center overflow-auto" style={{ maxWidth: "800px", maxHeight: "450px", margin: "0 auto" }}>
              <Cropper
                ref={cropperRef}
                src={imageSrc}
                aspectRatio={1}
                guides={false}
                cropBoxResizable={false}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between w-full">
          <Button type="button" onClick={updateImage}>
            {translate("atomic.image.updateImage", { _: "Atualizar imagem" })}
          </Button>
          <Button type="button" variant="destructive" onClick={deleteImage}>
            {translate("ra.action.delete", { _: "Excluir" })}
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("atomic.image.unsavedChanges", {
                _: "Descartar alterações?",
              })}
            </DialogTitle>
            <DialogDescription>
              {translate("atomic.image.unsavedChangesHint", {
                _: "Há alterações não salvas.",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-between w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              {translate("ra.action.cancel", { _: "Continuar editando" })}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDiscard}
            >
              {translate("ra.action.delete", { _: "Descartar" })}
            </Button>
            <Button type="button" onClick={updateImage}>
              {translate("atomic.image.save", { _: "Salvar" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ImageEditorField;

export interface ImageEditorFieldProps {
  source: string;
  width?: number;
  height?: number;
  type?: "avatar" | "image";
  onSave?: any;
  linkPosition?: "right" | "bottom" | "none";
  backgroundImageColor?: string;
  className?: string;
  emptyText?: string;
}

export interface ImageEditorDialogProps extends ImageEditorFieldProps {
  open: boolean;
  onClose: () => void;
}
